import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Divider,
  alpha,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Store as StoreIcon,
  CalendarToday as CalendarIcon,
  Inventory2 as InventoryIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
  BranchTransfer,
  CreateBranchTransferDto,
  UpdateBranchTransferDto,
} from '../../types/branchTransfers.types';
import { branchTransfersService } from '../../services/branchTransfers.service';
import { branchesService } from '../../services/branches.service';
import { itemsService, itemUnitsService, unitsService } from '../../services/items.service';
import { Branch } from '../../types/branches.types';
import { Item } from '../../types/items.types';
import axiosInstance from '../../services/axiosInstance';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';

const translations = {
  en: {
    addBranchTransfer: 'Add Branch Transfer',
    editBranchTransfer: 'Edit Branch Transfer',
    transferNumber: 'Transfer Number',
    transferDate: 'Transfer Date',
    fromBranch: 'From Branch',
    toBranch: 'To Branch',
    details: 'Transfer Details',
    item: 'Item',
    unit: 'Unit',
    quantity: 'Quantity',
    addLine: 'Add Line',
    save: 'Save',
    cancel: 'Cancel',
    required: 'Required',
    notes: 'Notes',
    selectFromBranch: 'Select Source Branch',
    selectToBranch: 'Select Destination Branch',
    selectItem: 'Select Item',
    transferInfo: 'Transfer Information',
    available: 'Available',
  },
  ar: {
    addBranchTransfer: 'إضافة تحويل بين فروع',
    editBranchTransfer: 'تعديل تحويل بين فروع',
    transferNumber: 'رقم التحويل',
    transferDate: 'تاريخ التحويل',
    fromBranch: 'من الفرع',
    toBranch: 'إلى الفرع',
    details: 'تفاصيل التحويل',
    item: 'الصنف',
    unit: 'الوحدة',
    quantity: 'الكمية',
    addLine: 'إضافة سطر',
    save: 'حفظ',
    cancel: 'إلغاء',
    required: 'مطلوب',
    notes: 'ملاحظات',
    selectFromBranch: 'اختر الفرع المصدر',
    selectToBranch: 'اختر الفرع الوجهة',
    selectItem: 'اختر الصنف',
    transferInfo: 'معلومات التحويل',
    available: 'المتوفر',
  },
};

interface BranchTransferDialogProps {
  open: boolean;
  onClose: () => void;
  branchTransfer?: BranchTransfer;
  onSave: () => void;
}

interface DetailLine {
  id: string;
  itemId: number | null;
  item: Item | null;
  unitId: number | null;
  unit: any | null;
  availableUnits: any[];
  quantity: number;
  availableQuantity: number | null;
  notes: string;
}

const BranchTransferDialog: React.FC<BranchTransferDialogProps> = ({
  open,
  onClose,
  branchTransfer,
  onSave,
}) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const isEditMode = !!branchTransfer;
  const dateLocked = isEditMode && !isSuperAdmin;

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [formData, setFormData] = useState({
    transferNumber: '',
    transferDate: new Date().toISOString().split('T')[0],
    fromBranchId: null as number | null,
    toBranchId: null as number | null,
    notes: '',
  });

  // قائمتين منفصلتين: فروع المستخدم (المصدر) وكل الفروع (الوجهة)
  const [userBranches, setUserBranches] = useState<Branch[]>([]);
  const [allBranches, setAllBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<Item[]>([]);

  const [selectedFromBranch, setSelectedFromBranch] = useState<Branch | null>(null);
  const [selectedToBranch, setSelectedToBranch] = useState<Branch | null>(null);

  const [detailLines, setDetailLines] = useState<DetailLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);

  useEffect(() => {
    if (open) {
      loadMasterData();
    }
  }, [open]);

  useEffect(() => {
    if (open && userBranches.length > 0 && allBranches.length > 0 && items.length > 0) {
      if (branchTransfer) {
        loadBranchTransfer();
      } else {
        loadNewTransferNumber();
        addNewLine();
      }
    }
  }, [open, branchTransfer, userBranches, allBranches, items]);

  // Reload available stock when From Branch changes
  useEffect(() => {

    if (selectedFromBranch && detailLines.length > 0) {
      const reloadAllStock = async () => {
        for (const line of detailLines) {
          if (line.itemId && line.unitId) {
            try {
              const stockInfo = await branchTransfersService.getAvailableStock(
                line.itemId,
                line.unitId,
                selectedFromBranch.branchId
              );
              updateLine(line.id, 'availableQuantity', stockInfo.availableQuantity || 0);
            } catch (error) {
              console.error(`❌ Failed to load stock for line ${line.id}:`, error);
              updateLine(line.id, 'availableQuantity', 0);
            }
          }
        }
      };
      reloadAllStock();
    } else if (!selectedFromBranch) {
      setDetailLines(prev =>
        prev.map(line => ({ ...line, availableQuantity: null }))
      );
    }
  }, [selectedFromBranch?.branchId]);

  const loadMasterData = async () => {
    try {
      const [userBranchesData, allBranchesResponse, itemsData, settingsData] = await Promise.all([
        branchesService.getAll(),
        axiosInstance.get('/Branches?includeAllBranches=true&branchType=Operational'),
        itemsService.getAll(),
        axiosInstance.get('/SystemSettings/AllowNegativeStock').catch(() => ({ data: null })),
      ]);

      if (settingsData.data && settingsData.data.value !== undefined) {
        setAllowNegativeStock(settingsData.data.value === 'true');
      }

      // استبعاد فروع المخزن من كلا القائمتين
      setUserBranches(userBranchesData.filter((b: Branch) => b.branchType === 'Operational'));
      setAllBranches(allBranchesResponse.data);
      setItems(itemsData);
    } catch (error) {
      console.error('❌ Failed to load master data:', error);
    }
  };

  const loadBranchTransfer = async () => {

    if (!branchTransfer) {
      return;
    }


    setFormData({
      transferNumber: branchTransfer.transferNumber,
      transferDate: branchTransfer.transferDate.split('T')[0],
      fromBranchId: branchTransfer.fromBranchId,
      toBranchId: branchTransfer.toBranchId,
      notes: branchTransfer.notes || '',
    });

    const fromBranch = userBranches.find(b => b.branchId === branchTransfer.fromBranchId)
      || allBranches.find(b => b.branchId === branchTransfer.fromBranchId);
    const toBranch = allBranches.find(b => b.branchId === branchTransfer.toBranchId);


    setSelectedFromBranch(fromBranch || null);
    setSelectedToBranch(toBranch || null);


    const linesPromises = branchTransfer.details.map(async (detail) => {

      let availableUnits: any[] = [];
      let selectedUnit = null;
      let availableQty = null;

      try {
        const units = await itemUnitsService.getItemUnits(detail.itemId);
        availableUnits = units;
        selectedUnit = units.find(u => u.unitId === detail.unitId) || null;

        if (fromBranch) {
          try {
            const stockInfo = await branchTransfersService.getAvailableStock(
              detail.itemId,
              detail.unitId,
              fromBranch.branchId
            );
            availableQty = stockInfo.availableQuantity || 0;
          } catch (error) {
            console.error(`    ❌ Failed to load stock for item ${detail.itemId}:`, error);
            availableQty = 0;
          }
        }
      } catch (error) {
        console.error(`    ❌ Failed to load units for item ${detail.itemId}:`, error);
      }

      return {
        id: `${detail.branchTransferDetailId}`,
        itemId: detail.itemId,
        item: {
          itemId: detail.itemId,
          itemCode: detail.itemCode,
          itemName: detail.itemName,
          itemNameAr: detail.itemNameAr,
        } as Item,
        unitId: detail.unitId,
        unit: selectedUnit,
        availableUnits: availableUnits,
        quantity: detail.quantity,
        availableQuantity: availableQty,
        notes: detail.notes || '',
      };
    });

    const lines = await Promise.all(linesPromises);
    setDetailLines(lines);
  };

  const loadNewTransferNumber = async () => {
    try {
      const transferNumber = await branchTransfersService.generateTransferNumber();
      setFormData((prev) => ({ ...prev, transferNumber }));
    } catch (error) {
      console.error('Failed to generate transfer number:', error);
    }
  };

  const addNewLine = () => {
    const newLine: DetailLine = {
      id: Date.now().toString(),
      itemId: null,
      item: null,
      unitId: null,
      unit: null,
      availableUnits: [],
      quantity: 1,
      availableQuantity: null,
      notes: '',
    };
    setDetailLines((prev) => [...prev, newLine]);
  };

  const removeLine = (id: string) => {
    setDetailLines((prev) => prev.filter((line) => line.id !== id));
  };

  const updateLine = (id: string, field: keyof DetailLine, value: any) => {
    setDetailLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  const fetchAvailableStock = async (lineId: string, itemId: number, unitId: number, branchId: number) => {
    try {
      const stockInfo = await branchTransfersService.getAvailableStock(itemId, unitId, branchId);
      updateLine(lineId, 'availableQuantity', stockInfo.availableQuantity || 0);
    } catch (error) {
      console.error('❌ Failed to fetch available stock:', error);
      updateLine(lineId, 'availableQuantity', 0);
    }
  };

  const handleUnitChange = async (lineId: string, newUnit: any | null) => {
    updateLine(lineId, 'unit', newUnit);
    updateLine(lineId, 'unitId', newUnit?.unitId || null);

    const line = detailLines.find(l => l.id === lineId);
    if (newUnit && line?.itemId && selectedFromBranch) {
      await fetchAvailableStock(lineId, line.itemId, newUnit.unitId, selectedFromBranch.branchId);
    } else {
      updateLine(lineId, 'availableQuantity', null);
    }
  };

  const handleItemChange = async (lineId: string, newItem: Item | null) => {

    if (!newItem) {
      updateLine(lineId, 'item', null);
      updateLine(lineId, 'itemId', null);
      updateLine(lineId, 'availableUnits', []);
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      updateLine(lineId, 'availableQuantity', null);
      return;
    }

    updateLine(lineId, 'item', newItem);
    updateLine(lineId, 'itemId', newItem.itemId);

    try {
      let units = await itemUnitsService.getItemUnits(newItem.itemId);

      const hasBaseUnit = units.some(u => u.unitId === newItem.baseUnitId);

      if (!hasBaseUnit && newItem.baseUnitId) {
        try {
          const allUnits = await unitsService.getAll();
          const baseUnit = allUnits.find(u => u.unitId === newItem.baseUnitId);

          if (baseUnit) {
            const baseUnitAsItemUnit = {
              itemUnitId: 0,
              itemId: newItem.itemId,
              unitId: baseUnit.unitId,
              unitName: baseUnit.unitName,
              unitNameAr: baseUnit.unitNameAr,
              unitCode: baseUnit.unitCode,
              conversionFactor: 1,
              defaultSalePrice: 0,
              defaultPurchasePrice: 0,
              isDefaultSale: true,
              isDefaultPurchase: true,
            };

            units = [baseUnitAsItemUnit, ...units];
          }
        } catch (error) {
          console.error('❌ Failed to load base unit from Units:', error);
        }
      }

      if (units.length > 0) {
        let defaultUnit = units.find(u => u.unitId === newItem.baseUnitId);

        if (!defaultUnit) {
          defaultUnit = units.find(u => u.isDefaultPurchase);
        }

        if (!defaultUnit) {
          defaultUnit = units[0];
        }


        updateLine(lineId, 'availableUnits', units);
        updateLine(lineId, 'unit', defaultUnit);
        updateLine(lineId, 'unitId', defaultUnit.unitId);

        if (selectedFromBranch) {
          await fetchAvailableStock(lineId, newItem.itemId, defaultUnit.unitId, selectedFromBranch.branchId);
        }
      } else {
        try {
          const allUnits = await unitsService.getAll();
          const baseUnit = allUnits.find(u => u.unitId === newItem.baseUnitId);

          if (baseUnit) {
            const baseUnitAsItemUnit = {
              itemUnitId: 0,
              itemId: newItem.itemId,
              unitId: baseUnit.unitId,
              unitName: baseUnit.unitName,
              unitNameAr: baseUnit.unitNameAr,
              unitCode: baseUnit.unitCode,
              conversionFactor: 1,
              defaultSalePrice: 0,
              defaultPurchasePrice: 0,
              isDefaultSale: true,
              isDefaultPurchase: true,
            };

            updateLine(lineId, 'availableUnits', [baseUnitAsItemUnit]);
            updateLine(lineId, 'unit', baseUnitAsItemUnit);
            updateLine(lineId, 'unitId', baseUnit.unitId);

            if (selectedFromBranch) {
              await fetchAvailableStock(lineId, newItem.itemId, baseUnit.unitId, selectedFromBranch.branchId);
            }
          } else {
            console.error('❌ Base unit not found!');
            updateLine(lineId, 'availableUnits', []);
            updateLine(lineId, 'unit', null);
            updateLine(lineId, 'unitId', null);
            updateLine(lineId, 'availableQuantity', null);
          }
        } catch (error) {
          console.error('❌ Failed to load base unit:', error);
          updateLine(lineId, 'availableUnits', []);
          updateLine(lineId, 'unit', null);
          updateLine(lineId, 'unitId', null);
          updateLine(lineId, 'availableQuantity', null);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load units:', error);
      updateLine(lineId, 'availableUnits', []);
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      updateLine(lineId, 'availableQuantity', null);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fromBranchId) {
      newErrors.fromBranchId = t.required;
    }
    if (!formData.toBranchId) {
      newErrors.toBranchId = t.required;
    }
    if (formData.fromBranchId === formData.toBranchId) {
      newErrors.toBranchId = 'Source and destination must be different';
    }
    if (detailLines.length === 0) {
      newErrors.details = 'At least one detail line is required';
    }
    detailLines.forEach((line, index) => {
      if (!line.itemId) {
        newErrors[`item_${index}`] = t.required;
      }
      if (!line.unitId) {
        newErrors[`unit_${index}`] = t.required;
      }
      if (line.quantity <= 0) {
        newErrors[`quantity_${index}`] = 'Must be greater than 0';
      }
      if (!allowNegativeStock && line.availableQuantity !== null && line.quantity > line.availableQuantity) {
        newErrors[`quantity_${index}`] = isRTL
          ? `الكمية المطلوبة (${line.quantity}) أكبر من المتوفر (${line.availableQuantity})`
          : `Requested quantity (${line.quantity}) exceeds available stock (${line.availableQuantity})`;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error(isRTL ? 'يرجى تصحيح الأخطاء قبل الحفظ' : 'Please correct errors before saving');
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Guard against saving with an empty transfer number (the silent auto-generate
    // failure pattern that corrupted PurchaseInvoice).
    if (!branchTransfer && !formData.transferNumber?.trim()) {
      toast.error(isRTL
        ? 'رقم التحويل فارغ — أعد فتح النموذج لتوليد رقم جديد.'
        : 'Transfer number is empty — close and reopen the form to generate a new number.');
      await loadNewTransferNumber();
      return;
    }

    setLoading(true);
    try {
      if (branchTransfer) {
        const updateDto: UpdateBranchTransferDto = {
          branchTransferId: branchTransfer.branchTransferId,
          transferDate: formData.transferDate,
          fromBranchId: formData.fromBranchId!,
          toBranchId: formData.toBranchId!,
          status: branchTransfer.status,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            branchTransferDetailId: (() => { const p = parseInt(line.id); return !isNaN(p) && p > 0 && p < 2147483647 ? p : 0; })(),
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            quantity: line.quantity,
            notes: line.notes || undefined,
          })),
        };
        await branchTransfersService.update(branchTransfer.branchTransferId, updateDto);
      } else {
        const createDto: CreateBranchTransferDto = {
          transferNumber: formData.transferNumber,
          transferDate: formData.transferDate,
          fromBranchId: formData.fromBranchId!,
          toBranchId: formData.toBranchId!,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            quantity: line.quantity,
            notes: line.notes || undefined,
          })),
        };
        await branchTransfersService.create(createDto);
      }
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save branch transfer:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      transferNumber: '',
      transferDate: new Date().toISOString().split('T')[0],
      fromBranchId: null,
      toBranchId: null,
      notes: '',
    });
    setDetailLines([]);
    setSelectedFromBranch(null);
    setSelectedToBranch(null);
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '95vh',
          borderRadius: 3,
          bgcolor: 'background.default',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: isDark
            ? 'linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)'
            : 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
          color: 'white',
          py: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <TransferIcon sx={{ fontSize: 36 }} />
        <Box component="span" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          {branchTransfer ? t.editBranchTransfer : t.addBranchTransfer}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Transfer Info Card */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            background: isDark
              ? 'linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)'
              : 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <InventoryIcon sx={{ color: 'white' }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              {t.transferInfo}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: Transfer Number + Date */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label={t.transferNumber}
                  value={formData.transferNumber}
                  disabled
                  error={!!errors.transferNumber}
                  helperText={errors.transferNumber}
                  sx={{
                    backgroundColor: isDark ? alpha('#ffffff', 0.1) : 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'transparent' },
                    }
                  }}
                  InputProps={{
                    startAdornment: <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t.transferDate}
                    value={formData.transferDate ? new Date(formData.transferDate) : null}
                    onChange={(date) => setFormData({ ...formData, transferDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                    format="dd/MM/yyyy"
                    views={['year', 'month', 'day']}
                    openTo="day"
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={dateLocked}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!errors.transferDate, helperText: errors.transferDate, sx: { backgroundColor: isDark ? alpha('#ffffff', 0.1) : 'rgba(255, 255, 255, 0.9)', borderRadius: 1, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'transparent' } } } } }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>

            {/* Row 2: Branches */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
              {/* من الفرع: فروع المستخدم فقط */}
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  options={userBranches}
                  getOptionLabel={(option) => (isRTL ? option.branchNameAr : option.branchName)}
                  value={selectedFromBranch}
                  onChange={(_, newValue) => {
                    setSelectedFromBranch(newValue);
                    setFormData({ ...formData, fromBranchId: newValue?.branchId || null });
                    // Clear "To Branch" if it's the same as the newly selected "From Branch"
                    if (newValue && selectedToBranch?.branchId === newValue.branchId) {
                      setSelectedToBranch(null);
                      setFormData(prev => ({ ...prev, fromBranchId: newValue.branchId, toBranchId: null }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t.fromBranch}
                      required
                      error={!!errors.fromBranchId}
                      helperText={errors.fromBranchId}
                      sx={{
                        backgroundColor: isDark ? alpha('#ffffff', 0.1) : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'transparent' },
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <StoreIcon sx={{ mr: 1, color: 'error.main' }} />
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Box>

              <TransferIcon
                sx={{
                  fontSize: 40,
                  color: 'white',
                  display: { xs: 'none', sm: 'block' }
                }}
              />

              {/* إلى الفرع: كل الفروع ما عدا الفرع المصدر */}
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  options={allBranches.filter(b => b.branchId !== formData.fromBranchId)}
                  getOptionLabel={(option) => (isRTL ? option.branchNameAr : option.branchName)}
                  value={selectedToBranch}
                  onChange={(_, newValue) => {
                    setSelectedToBranch(newValue);
                    setFormData({ ...formData, toBranchId: newValue?.branchId || null });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t.toBranch}
                      required
                      error={!!errors.toBranchId}
                      helperText={errors.toBranchId}
                      sx={{
                        backgroundColor: isDark ? alpha('#ffffff', 0.1) : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'transparent' },
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <StoreIcon sx={{ mr: 1, color: 'success.main' }} />
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        <Divider sx={{ my: 3 }}>
          <Chip
            label={t.details}
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              px: 2,
              background: isDark
                ? 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)'
                : 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              color: 'white'
            }}
          />
        </Divider>

        {/* Details Table */}
        <Box ref={gridRef}>
        <TableContainer
          component={Paper}
          elevation={3}
          sx={{
            mb: 2,
            maxHeight: { xs: 300, sm: 400, md: 500 },
            overflowX: 'auto',
            borderRadius: 2,
            border: '2px solid',
            borderColor: isDark ? alpha('#42a5f5', 0.3) : alpha('#1976d2', 0.2),
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{
                background: isDark
                  ? 'linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)'
                  : 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
              }}>
                <TableCell sx={{ minWidth: 250, color: 'white', fontWeight: 700 }}>{t.item}</TableCell>
                <TableCell sx={{ minWidth: 120, color: 'white', fontWeight: 700 }}>{t.unit}</TableCell>
                <TableCell sx={{ minWidth: 100, color: 'white', fontWeight: 700 }}>{t.available}</TableCell>
                <TableCell sx={{ minWidth: 120, color: 'white', fontWeight: 700 }}>{t.quantity}</TableCell>
                <TableCell sx={{ minWidth: 200, color: 'white', fontWeight: 700 }}>{t.notes}</TableCell>
                <TableCell sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailLines.map((line, index) => (
                <TableRow
                  key={line.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: isDark ? alpha('#42a5f5', 0.1) : alpha('#1976d2', 0.05),
                    }
                  }}
                >
                  <TableCell>
                    <Autocomplete
                      options={items}
                      getOptionLabel={(option) => (isRTL ? option.itemNameAr : option.itemName)}
                      value={line.item}
                      onChange={(_, newValue) => handleItemChange(line.id, newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          error={!!errors[`item_${index}`]}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      options={line.availableUnits}
                      getOptionLabel={(option) => (isRTL ? option.unitNameAr : option.unitName)}
                      value={line.unit}
                      onChange={(_, newValue) => handleUnitChange(line.id, newValue)}
                      disabled={!line.item || line.availableUnits.length === 0}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          error={!!errors[`unit_${index}`]}
                          placeholder={isRTL ? 'اختر الوحدة' : 'Select Unit'}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: line.availableQuantity !== null
                        ? (line.availableQuantity > 0
                            ? (isDark ? alpha('#4caf50', 0.2) : alpha('#4caf50', 0.1))
                            : (isDark ? alpha('#f44336', 0.2) : alpha('#f44336', 0.1)))
                        : 'transparent'
                    }}>
                      {line.availableQuantity !== null ? (
                        <>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              color: line.availableQuantity > 0
                                ? (isDark ? '#81c784' : '#2e7d32')
                                : (isDark ? '#e57373' : '#c62828')
                            }}
                          >
                            {line.availableQuantity.toFixed(3)}
                          </Typography>
                          {line.quantity > line.availableQuantity && (
                            <Typography
                              variant="caption"
                              sx={{ color: isDark ? '#ef5350' : '#d32f2f', fontWeight: 'bold' }}
                            >
                              ⚠️
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={line.quantity === 0 ? '' : String(line.quantity)}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d*\.?\d*$/.test(v)) {
                          updateLine(line.id, 'quantity', v === '' || v === '.' ? 0 : parseFloat(v));
                        }
                      }}
                      error={!!errors[`quantity_${index}`] || (line.availableQuantity !== null && line.quantity > line.availableQuantity)}
                      inputProps={{ inputMode: 'decimal' }}
                      helperText={
                        line.availableQuantity !== null && line.quantity > line.availableQuantity
                          ? (isRTL ? 'الكمية أكبر من المتوفر' : 'Exceeds available stock')
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={line.notes}
                      onChange={(e) => updateLine(line.id, 'notes', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeLine(line.id)}
                      sx={{
                        '&:hover': {
                          backgroundColor: isDark ? alpha('#f44336', 0.2) : alpha('#f44336', 0.1),
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>

        <Button
          startIcon={<AddIcon />}
          onClick={addNewLine}
          variant="contained"
          sx={{
            mb: 3,
            background: isDark
              ? 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)'
              : 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
            '&:hover': {
              background: isDark
                ? 'linear-gradient(45deg, #0d47a1 30%, #1565c0 90%)'
                : 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            }
          }}
          size="large"
        >
          {t.addLine}
        </Button>

        <TextField
          fullWidth
          label={t.notes}
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          multiline
          rows={3}
          size="medium"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: isDark ? alpha('#1976d2', 0.15) : alpha('#1976d2', 0.05),
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          size="large"
          variant="outlined"
          sx={{ minWidth: 120 }}
        >
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          size="large"
          sx={{
            minWidth: 120,
            background: isDark
              ? 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)'
              : 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
            '&:hover': {
              background: isDark
                ? 'linear-gradient(45deg, #2e7d32 30%, #388e3c 90%)'
                : 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
            }
          }}
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchTransferDialog;